import type { AuditRunStatus } from '../api/enrollment-overlap.types'

export const DEFAULT_PAGE = 1
export const DEFAULT_PAGE_SIZE = 25
/** The backend rejects anything above this with a 400. */
export const MAX_PAGE_SIZE = 200
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

export const RUN_STATUSES: readonly AuditRunStatus[] = [
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
]

/** Raw `page`/`pageSize`/`status` values as read from the URL query string. */
export interface RawPagingQuery {
  page?: unknown
  pageSize?: unknown
  status?: unknown
}

function firstValue(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value
}

function toInt(value: unknown): number | null {
  const first = firstValue(value)
  if (typeof first !== 'string' && typeof first !== 'number') return null
  const parsed = Number(first)
  return Number.isInteger(parsed) ? parsed : null
}

/**
 * Clamps URL values into what the backend accepts, so a hand-edited or stale
 * link degrades to a valid page instead of a 400.
 */
export function parsePaging(query: RawPagingQuery): {
  page: number
  pageSize: number
} {
  const page = toInt(query.page)
  const pageSize = toInt(query.pageSize)
  return {
    page: page !== null && page >= 1 ? page : DEFAULT_PAGE,
    pageSize:
      pageSize !== null && pageSize >= 1
        ? Math.min(pageSize, MAX_PAGE_SIZE)
        : DEFAULT_PAGE_SIZE,
  }
}

export function parseStatus(value: unknown): AuditRunStatus | undefined {
  const first = firstValue(value)
  return RUN_STATUSES.find((status) => status === first)
}

/**
 * Builds the query-string patch for a paging change. Defaults are omitted so
 * the URL stays short; `undefined` removes a key.
 */
export function buildPagingPatch(
  page: number,
  pageSize: number
): { page: string | undefined; pageSize: string | undefined } {
  return {
    page: page === DEFAULT_PAGE ? undefined : String(page),
    pageSize: pageSize === DEFAULT_PAGE_SIZE ? undefined : String(pageSize),
  }
}
